// A scan's report: its state first (the live view while it has not ended; a tag beside the title when
// it completed, a notice when it completed partially, what happened when it could not be completed),
// then, when there is a result, everything the scan found (the summary, the problems and the pages). It sits inside the website's page, under its tabs; one
// page at a time is read in the "Pages" tab.
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  can,
  meQuery,
  type ScanDetail,
  type ScanIssue,
  scanIssuesQuery,
  scanQuery,
  scansQuery,
  siteQuery,
} from '../api';
import { scannedFamilies } from '../page-families';
import { ReportButton } from '../report-button';
import { groupProblems, isFullyDiscarded, problemsByPage, splitCategories } from '../results-logic';
import { byWorst } from '../scan-logic';
import { Message, useArrivalFocus, usePageTitle } from '../ui';
import { rightsOf } from './follow-state';
import { PreviousNotice, ResultsView } from './results';
import { LiveScanView } from './scan-live';
import { PageFamilyCard, useDate } from './scan-parts';
import {
  FailedStatusCard,
  PartialNotice,
  RefreshError,
  refreshOf,
  ScanStatusRegion,
  StatusTag,
  useScanAnnouncement,
} from './scan-status';

export function ScanPage() {
  const { t } = useTranslation();
  const date = useDate();
  const { siteId, scanId } = useParams({ from: '/sites/$siteId/scans/$scanId' });
  const site = useQuery({ ...siteQuery(siteId), retry: false });
  const scan = useQuery({ ...scanQuery(scanId), retry: false });
  const issues = useQuery({ ...scanIssuesQuery(scanId), enabled: scan.data?.status === 'done' });
  const { data: me } = useQuery(meQuery);
  // The latest scan of the website: a result that is not it says so.
  const latest = useQuery(scansQuery(siteId)).data?.[0];
  const title = scan.data ? t('scan.title', { date: date(scan.data.createdAt) }) : undefined;
  const heading = useArrivalFocus(Boolean(scan.data));
  usePageTitle(title, site.data?.name);
  const refresh = refreshOf(scan);
  const announcement = useScanAnnouncement(scanId, scan.data, scan.isSuccess, refresh.failed);

  if (scan.isPending) {
    return <p role="status">{t('app.loading')}</p>;
  }
  // Nothing was ever read: a missing scan, or a state that could not be read (which is not a failed scan).
  if (!scan.data) {
    const missing = scan.error instanceof ApiError && scan.error.status === 404;
    return (
      <div className="flex flex-col items-start gap-4">
        {missing ? <p>{t('notFound.text')}</p> : <RefreshError refresh={refresh} />}
        <p className="mt-2">
          <Link to="/sites/$siteId" params={{ siteId }}>
            {t('scan.back')}
          </Link>
        </p>
      </div>
    );
  }

  const data = scan.data;
  const url = site.data?.url ?? '';
  const active = data.outcome === 'queued' || data.outcome === 'running';
  return (
    <div className="scan-report">
      <h2 ref={heading} tabIndex={-1} className="report-title">
        {title}
      </h2>
      {!active && data.outcome !== 'failed' && (
        <p className="mb-4">
          <StatusTag outcome={data.outcome} />
        </p>
      )}
      <p className="mb-6">
        <Link to="/sites/$siteId" params={{ siteId }} className="back-link">
          {t('scan.back')}
        </Link>
      </p>
      <ScanStatusRegion announcement={announcement} scan={data} siteId={siteId} onSamePage />
      {active ? (
        <LiveScanView scan={data} live={data.live} url={url} refresh={refresh} headingLevel={2} />
      ) : (
        <>
          {refresh.failed && <RefreshError refresh={refresh} />}
          {data.outcome === 'failed' && (
            <div className="mb-6">
              <FailedStatusCard
                scan={data}
                url={url}
                siteId={siteId}
                pages={data.pages}
                mayConfigure={can(me, 'site', 'update') || can(me, 'site', 'delete')}
                prepareLink={can(me, 'site', 'scan')}
                headingLevel={2}
              />
            </div>
          )}
          {data.outcome === 'partial' && (
            <div className="mb-6">
              <PartialNotice
                scan={data}
                pages={data.pages}
                siteId={siteId}
                mayComplete={can(me, 'site', 'scan')}
              />
            </div>
          )}
        </>
      )}
      {data.outcome === 'complete' || data.outcome === 'partial' ? (
        <>
          {latest && latest.id !== data.id && <PreviousNotice latest={latest} siteId={siteId} />}
          <div className="mb-6">
            <ReportButton scanId={data.id} />
          </div>
          {issues.isPending ? (
            <p role="status">{t('app.loading')}</p>
          ) : issues.isError ? (
            <Message tone="error">{t('errors.generic')}</Message>
          ) : (
            <>
              <ResultsView scan={data} issues={issues.data} siteId={siteId} rights={rightsOf(me)} />
              <PagesMap scan={data} siteId={siteId} issues={issues.data} />
            </>
          )}
        </>
      ) : null}
    </div>
  );
}

/** The pages of the scan as compact cards, the worst first: the way into each page's own results. */
function PagesMap({ scan, siteId, issues }: { scan: ScanDetail; siteId: string; issues: ScanIssue[] }) {
  const { t } = useTranslation();
  const pages = [...scan.pages].sort(byWorst);
  const problems = useMemo(
    () => groupProblems(splitCategories(issues).problems).filter((problem) => !isFullyDiscarded(problem)),
    [issues],
  );
  const found = useMemo(() => problemsByPage(problems, scan.pages), [problems, scan.pages]);
  const families = useMemo(() => scannedFamilies(pages), [pages]);
  return (
    <section className="chart-card" aria-labelledby="mapa-paginas">
      <div className="card-head">
        <h2 id="mapa-paginas">{t('scan.map.title')}</h2>
        <p>{t('scan.map.hint')}</p>
      </div>
      {!scan.capturesAvailable && <p className="text-sm text-ink-muted">{t('scan.map.noCaptures')}</p>}
      <ul className="page-cards">
        {families.map((family) => (
          <li key={family.key}>
            <PageFamilyCard
              scanId={scan.id}
              siteId={siteId}
              family={family}
              found={(key) => found.get(key)}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
